import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPaginated, getOne, post, patch, put, del } from '@/lib/apiV2';
import type {
  School,
  AcademicYear,
  Class,
  Section,
  PagedResult,
} from '@/features/auth/types';

// ---- Subjects / Rooms / Terms (gen.Subject / gen.Room / gen.Term mirrors) ---
// Added for features/exams (grading config + schedule builder + seating need
// school subjects/rooms and year terms); co-located here rather than in
// features/exams since they are generic academic reads other features may
// reuse, same precedent as Class/Section above.

export interface Subject {
  id: string;
  org_id: string;
  school_id: string;
  code: string | null;
  name: string;
  department_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Room {
  id: string;
  org_id: string;
  school_id: string;
  name: string;
  type: string | null;
  capacity: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Term {
  id: string;
  org_id: string;
  academic_year_id: string;
  name: string;
  starts_on: string;
  ends_on: string;
  position: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// ---- School periods (gen.SchoolPeriod) --------------------------------------
//
// GOTCHA (verified against pgx v5.10 gen.SchoolPeriod): starts_at / ends_at
// are pgtype.Time columns and pgtype.Time implements NO MarshalJSON, so
// encoding/json serializes the bare exported struct fields — `{"Microseconds":
// 30600000000, "Valid": true}`, NOT an "HH:MM" string. periodTimeToHHMM in
// constants.tsx decodes this; the PUT upsert takes "HH:MM" back (parsePgTime
// on the server expects "15:04").
export interface PgTime {
  Microseconds: number;
  Valid: boolean;
}

export interface SchoolPeriod {
  id: string;
  org_id: string;
  school_id: string;
  period_no: number;
  starts_at: PgTime;
  ends_at: PgTime;
  created_at: string;
  updated_at: string;
}

// ---- Section-subject-teacher matrix (gen.ListSectionSubjectTeachersRow) ------
//
// The SST join row: one teacher per (section, subject). teacher_name/subject_name
// come from the join, so the matrix can render names after an assignment
// without a second fetch. Assign takes a raw teacher_staff_id (staff UUID).
export interface SectionSubjectTeacherRow {
  id: string;
  org_id: string;
  section_id: string;
  subject_id: string;
  subject_name: string;
  teacher_staff_id: string;
  teacher_name: string;
  created_at: string;
  updated_at: string;
}

// ---- Counselor class assignments (gen.CounselorClassAssignment /
// gen.GetCounselorSectionsRow) ------------------------------------------------
//
// counselor_class_assignments is a one-row-per-section table (upsert on
// section_id, see UpsertCounselorAssignment on the server — ON CONFLICT
// (section_id)). There is deliberately no "list assignments for a school"
// read: the only reads are the reverse lookup (a counselor's own sections)
// and the mutation's own response. So the UI cannot show "who is this
// section's counselor" on page load without first knowing a staff id — it
// can only confirm the *result* of an assignment made this session, or the
// *sections* a given staff id already counsels (used as a live preview while
// typing a candidate staff id, mirroring the reverse-lookup use case in the
// gap notes).
export interface CounselorClassAssignment {
  id: string;
  org_id: string;
  section_id: string;
  counselor_staff_id: string;
  created_at: string;
  updated_at: string;
}

export interface CounselorSectionRow {
  id: string;
  org_id: string;
  school_id: string;
  class_id: string;
  academic_year_id: string;
  name: string;
  class_teacher_staff_id: string | null;
  capacity: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  class_name: string;
}

// ---- Query keys -------------------------------------------------------------

export const schoolKeys = {
  all: ['schools'] as const,
  list: () => [...schoolKeys.all, 'list'] as const,
};

export const academicKeys = {
  years: (schoolId: string) => ['academic', 'years', schoolId] as const,
  classes: (schoolId: string) => ['academic', 'classes', schoolId] as const,
  sections: (schoolId: string, classId?: string, yearId?: string) =>
    ['academic', 'sections', schoolId, classId ?? 'all', yearId ?? 'all'] as const,
  subjects: (schoolId: string) => ['academic', 'subjects', schoolId] as const,
  rooms: (schoolId: string) => ['academic', 'rooms', schoolId] as const,
  terms: (schoolId: string, yearId: string) => ['academic', 'terms', schoolId, yearId] as const,
  periods: (schoolId: string) => ['academic', 'periods', schoolId] as const,
  sectionTeachers: (schoolId: string, sectionId: string) =>
    ['academic', 'section-teachers', schoolId, sectionId] as const,
  sectionTimetable: (schoolId: string, sectionId: string, yearId: string) =>
    ['academic', 'section-timetable', schoolId, sectionId, yearId] as const,
  schoolTimetable: (schoolId: string, yearId: string) =>
    ['academic', 'school-timetable', schoolId, yearId] as const,
  substitutes: (schoolId: string, date: string) =>
    ['academic', 'substitutes', schoolId, date] as const,
  myTimetable: (yearId?: string) => ['academic', 'my-timetable', yearId ?? 'current'] as const,
  electiveGroups: (schoolId: string, classId?: string, yearId?: string) =>
    ['academic', 'elective-groups', schoolId, classId ?? 'all', yearId ?? 'all'] as const,
  electiveGroupSubjects: (schoolId: string, groupId: string) =>
    ['academic', 'elective-group-subjects', schoolId, groupId] as const,
  counselorSections: (schoolId: string, staffId: string) =>
    ['academic', 'counselor-sections', schoolId, staffId] as const,
};

// ---- Schools ----------------------------------------------------------------

export function useSchools(options: { enabled?: boolean } = {}) {
  return useQuery<PagedResult<School>>({
    queryKey: schoolKeys.list(),
    queryFn: () => getPaginated<School>('/schools', { per_page: 100 }),
    staleTime: 1000 * 60 * 10,
    enabled: options.enabled ?? true,
  });
}

// ---- Academic years ---------------------------------------------------------

export function useAcademicYears(schoolId: string | undefined) {
  return useQuery<PagedResult<AcademicYear>>({
    queryKey: academicKeys.years(schoolId ?? ''),
    queryFn: () =>
      getPaginated<AcademicYear>(`/schools/${schoolId}/academic-years`, { per_page: 100 }),
    enabled: Boolean(schoolId),
    staleTime: 1000 * 60 * 5,
  });
}

export function useSetCurrentYear(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<AcademicYear, Error, string>({
    mutationFn: (yearId) =>
      post<Record<string, never>, AcademicYear>(
        `/schools/${schoolId}/academic-years/${yearId}/set-current`,
        {},
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.years(schoolId) });
    },
  });
}

export interface AcademicYearInput {
  name: string;
  starts_on: string; // "YYYY-MM-DD"
  ends_on: string; // "YYYY-MM-DD"
}

export function useCreateYear(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<AcademicYear, Error, AcademicYearInput>({
    mutationFn: (input) =>
      post<AcademicYearInput, AcademicYear>(`/schools/${schoolId}/academic-years`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.years(schoolId) });
    },
  });
}

export function useUpdateYear(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<AcademicYear, Error, { id: string; patch: Partial<AcademicYearInput> }>({
    mutationFn: ({ id, patch: body }) =>
      patch<Partial<AcademicYearInput>, AcademicYear>(
        `/schools/${schoolId}/academic-years/${id}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.years(schoolId) });
    },
  });
}

export function useDeleteYear(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${schoolId}/academic-years/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.years(schoolId) });
    },
  });
}

// ---- Terms ------------------------------------------------------------------

export interface CreateTermInput {
  name: string;
  academic_year_id: string;
  starts_on: string;
  ends_on: string;
  position?: number;
}

export function useCreateTerm(schoolId: string, yearId: string) {
  const queryClient = useQueryClient();
  return useMutation<Term, Error, CreateTermInput>({
    mutationFn: (input) =>
      post<CreateTermInput, Term>(
        `/schools/${schoolId}/academic-years/${yearId}/terms`,
        input,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.terms(schoolId, yearId) });
    },
  });
}

export interface UpdateTermInput {
  name?: string;
  starts_on?: string;
  ends_on?: string;
  position?: number;
}

export function useUpdateTerm(schoolId: string, yearId: string) {
  const queryClient = useQueryClient();
  return useMutation<Term, Error, { id: string; patch: UpdateTermInput }>({
    mutationFn: ({ id, patch: body }) =>
      patch<UpdateTermInput, Term>(
        `/schools/${schoolId}/academic-years/${yearId}/terms/${id}`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.terms(schoolId, yearId) });
    },
  });
}

export function useDeleteTerm(schoolId: string, yearId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${schoolId}/academic-years/${yearId}/terms/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.terms(schoolId, yearId) });
    },
  });
}

// ---- Classes ----------------------------------------------------------------

export function useClasses(schoolId: string | undefined) {
  return useQuery<PagedResult<Class>>({
    queryKey: academicKeys.classes(schoolId ?? ''),
    queryFn: () =>
      getPaginated<Class>(`/schools/${schoolId}/classes`, { per_page: 100 }),
    enabled: Boolean(schoolId),
    staleTime: 1000 * 60 * 5,
  });
}

export interface ClassInput {
  name: string;
  level: number;
}

export function useCreateClass(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Class, Error, ClassInput>({
    mutationFn: (input) => post<ClassInput, Class>(`/schools/${schoolId}/classes`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.classes(schoolId) });
    },
  });
}

export function useUpdateClass(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Class, Error, { id: string; patch: Partial<ClassInput> }>({
    mutationFn: ({ id, patch: body }) =>
      patch<Partial<ClassInput>, Class>(`/schools/${schoolId}/classes/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.classes(schoolId) });
    },
  });
}

export function useDeleteClass(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${schoolId}/classes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.classes(schoolId) });
    },
  });
}

// ---- Sections ---------------------------------------------------------------
//
// Sections are per (class, academic year). yearId scopes the list; classId
// narrows to one class. Kept positional + optional so existing callers
// (useSections(schoolId) / useSections(schoolId, classId)) are unaffected.

export function useSections(schoolId: string | undefined, classId?: string, yearId?: string) {
  return useQuery<PagedResult<Section>>({
    queryKey: academicKeys.sections(schoolId ?? '', classId, yearId),
    queryFn: () =>
      getPaginated<Section>(`/schools/${schoolId}/sections`, {
        per_page: 200,
        ...(classId ? { class_id: classId } : {}),
        ...(yearId ? { academic_year_id: yearId } : {}),
      }),
    enabled: Boolean(schoolId),
    staleTime: 1000 * 60 * 5,
  });
}

export interface CreateSectionInput {
  class_id: string;
  academic_year_id: string;
  name: string;
  class_teacher_staff_id?: string | null;
  capacity?: number | null;
}

export interface UpdateSectionInput {
  name?: string;
  class_teacher_staff_id?: string | null;
  capacity?: number | null;
}

function invalidateSections(queryClient: ReturnType<typeof useQueryClient>, schoolId: string) {
  // Invalidate every (classId, yearId) variant for this school.
  queryClient.invalidateQueries({ queryKey: ['academic', 'sections', schoolId] });
}

export function useCreateSection(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Section, Error, CreateSectionInput>({
    mutationFn: (input) => post<CreateSectionInput, Section>(`/schools/${schoolId}/sections`, input),
    onSuccess: () => invalidateSections(queryClient, schoolId),
  });
}

export function useUpdateSection(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Section, Error, { id: string; patch: UpdateSectionInput }>({
    mutationFn: ({ id, patch: body }) =>
      patch<UpdateSectionInput, Section>(`/schools/${schoolId}/sections/${id}`, body),
    onSuccess: () => invalidateSections(queryClient, schoolId),
  });
}

export function useDeleteSection(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${schoolId}/sections/${id}`),
    onSuccess: () => invalidateSections(queryClient, schoolId),
  });
}

// ---- Counselor assignments ---------------------------------------------------
//
// PUT /schools/:schoolID/sections/:sectionID/counselor upserts the academic
// counselor for one section (settings.manage). GET
// /schools/:schoolID/counselors/:staffID/sections is the reverse read
// (timetable.view) — every section a given staff id currently counsels. See
// the CounselorClassAssignment / CounselorSectionRow comment above for why
// there is no forward "list assignments for this school" read.

/** GET a staff id's current counselor sections — used as a live preview while assigning. */
export function useCounselorSections(schoolId: string | undefined, staffId: string | undefined) {
  return useQuery<CounselorSectionRow[]>({
    queryKey: academicKeys.counselorSections(schoolId ?? '', staffId ?? ''),
    queryFn: () =>
      getOne<CounselorSectionRow[]>(`/schools/${schoolId}/counselors/${staffId}/sections`),
    enabled: Boolean(schoolId) && Boolean(staffId),
    staleTime: 1000 * 30,
  });
}

/** PUT /schools/:schoolID/sections/:sectionID/counselor — upsert by section (idempotent). */
export function useUpsertCounselorAssignment(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    CounselorClassAssignment,
    Error,
    { sectionId: string; counselorStaffId: string }
  >({
    mutationFn: ({ sectionId, counselorStaffId }) =>
      put<{ counselor_staff_id: string }, CounselorClassAssignment>(
        `/schools/${schoolId}/sections/${sectionId}/counselor`,
        { counselor_staff_id: counselorStaffId },
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: academicKeys.counselorSections(schoolId, vars.counselorStaffId),
      });
    },
  });
}

// ---- Subjects -----------------------------------------------------------------

export function useSubjects(schoolId: string | undefined) {
  return useQuery<PagedResult<Subject>>({
    queryKey: academicKeys.subjects(schoolId ?? ''),
    queryFn: () => getPaginated<Subject>(`/schools/${schoolId}/subjects`, { per_page: 200 }),
    enabled: Boolean(schoolId),
    staleTime: 1000 * 60 * 5,
  });
}

export interface CreateSubjectInput {
  name: string;
  code?: string | null;
  department_id?: string | null;
}

export interface UpdateSubjectInput {
  name?: string;
  code?: string | null;
  department_id?: string | null;
}

export function useCreateSubject(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Subject, Error, CreateSubjectInput>({
    mutationFn: (input) => post<CreateSubjectInput, Subject>(`/schools/${schoolId}/subjects`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.subjects(schoolId) });
    },
  });
}

export function useUpdateSubject(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Subject, Error, { id: string; patch: UpdateSubjectInput }>({
    mutationFn: ({ id, patch: body }) =>
      patch<UpdateSubjectInput, Subject>(`/schools/${schoolId}/subjects/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.subjects(schoolId) });
    },
  });
}

export function useDeleteSubject(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${schoolId}/subjects/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.subjects(schoolId) });
    },
  });
}

// ---- Rooms ----------------------------------------------------------------------

export function useRooms(schoolId: string | undefined) {
  return useQuery<PagedResult<Room>>({
    queryKey: academicKeys.rooms(schoolId ?? ''),
    queryFn: () => getPaginated<Room>(`/schools/${schoolId}/rooms`, { per_page: 200 }),
    enabled: Boolean(schoolId),
    staleTime: 1000 * 60 * 5,
  });
}

export type RoomType = 'classroom' | 'lab' | 'hall';

export interface CreateRoomInput {
  name: string;
  type?: RoomType | null;
  capacity?: number | null;
}

export interface UpdateRoomInput {
  name?: string;
  type?: RoomType | null;
  capacity?: number | null;
}

export function useCreateRoom(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Room, Error, CreateRoomInput>({
    mutationFn: (input) => post<CreateRoomInput, Room>(`/schools/${schoolId}/rooms`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.rooms(schoolId) });
    },
  });
}

export function useUpdateRoom(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<Room, Error, { id: string; patch: UpdateRoomInput }>({
    mutationFn: ({ id, patch: body }) =>
      patch<UpdateRoomInput, Room>(`/schools/${schoolId}/rooms/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.rooms(schoolId) });
    },
  });
}

export function useDeleteRoom(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${schoolId}/rooms/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.rooms(schoolId) });
    },
  });
}

// ---- Terms ------------------------------------------------------------------------

export function useTerms(schoolId: string | undefined, yearId: string | undefined) {
  return useQuery<Term[]>({
    queryKey: academicKeys.terms(schoolId ?? '', yearId ?? ''),
    queryFn: () => getOne<Term[]>(`/schools/${schoolId}/academic-years/${yearId}/terms`),
    enabled: Boolean(schoolId) && Boolean(yearId),
    staleTime: 1000 * 60 * 5,
  });
}

// ---- School periods (bell schedule) -----------------------------------------

export function useSchoolPeriods(schoolId: string | undefined) {
  return useQuery<SchoolPeriod[]>({
    queryKey: academicKeys.periods(schoolId ?? ''),
    queryFn: () => getOne<SchoolPeriod[]>(`/schools/${schoolId}/periods`),
    enabled: Boolean(schoolId),
    staleTime: 1000 * 60 * 5,
  });
}

export interface UpsertPeriodInput {
  period_no: number;
  starts_at: string; // "HH:MM"
  ends_at: string; // "HH:MM"
}

/** PUT /schools/:schoolID/periods — upsert by period_no (server ON CONFLICT). */
export function useUpsertPeriod(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<SchoolPeriod, Error, UpsertPeriodInput>({
    mutationFn: (input) => put<UpsertPeriodInput, SchoolPeriod>(`/schools/${schoolId}/periods`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.periods(schoolId) });
    },
  });
}

export function useDeletePeriod(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (periodNo) => del(`/schools/${schoolId}/periods/${periodNo}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.periods(schoolId) });
    },
  });
}

// ---- Section-subject-teacher matrix -----------------------------------------

export function useSectionTeachers(schoolId: string | undefined, sectionId: string | undefined) {
  return useQuery<SectionSubjectTeacherRow[]>({
    queryKey: academicKeys.sectionTeachers(schoolId ?? '', sectionId ?? ''),
    queryFn: () =>
      getOne<SectionSubjectTeacherRow[]>(`/schools/${schoolId}/sections/${sectionId}/teachers`),
    enabled: Boolean(schoolId) && Boolean(sectionId),
    staleTime: 1000 * 60 * 5,
  });
}

/** PUT /schools/:schoolID/sections/:sectionID/subjects/:subjectID/teacher. */
export function useAssignTeacher(schoolId: string, sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { subjectId: string; teacherStaffId: string }>({
    mutationFn: ({ subjectId, teacherStaffId }) =>
      put<{ teacher_staff_id: string }, unknown>(
        `/schools/${schoolId}/sections/${sectionId}/subjects/${subjectId}/teacher`,
        { teacher_staff_id: teacherStaffId },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: academicKeys.sectionTeachers(schoolId, sectionId),
      });
    },
  });
}

export function useRemoveTeacher(schoolId: string, sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (subjectId) =>
      del(`/schools/${schoolId}/sections/${sectionId}/subjects/${subjectId}/teacher`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: academicKeys.sectionTeachers(schoolId, sectionId),
      });
    },
  });
}

// ---- Timetable (gen.GetSectionTimetableRow / gen.GetSchoolTimetableRow) ------
//
// The rich join row: a slot with subject/teacher/room names + the period's
// pgtype.Time window (same `{Microseconds, Valid}` shape as SchoolPeriod —
// decode with periodTimeToHHMM). day_of_week is 1–6 (Mon–Sat), period_no is
// keyed to the bell schedule. GetSchoolTimetableRow adds section/class labels
// (school-wide occupancy, HLD B2).

export interface SectionTimetableRow {
  id: string;
  org_id: string;
  school_id: string;
  section_id: string;
  academic_year_id: string;
  day_of_week: number;
  period_no: number;
  subject_id: string;
  subject_name: string;
  teacher_staff_id: string;
  teacher_name: string;
  room_id: string | null;
  room_name: string | null;
  starts_at: PgTime;
  ends_at: PgTime;
  created_at: string;
  updated_at: string;
}

export interface SchoolTimetableRow extends SectionTimetableRow {
  section_name: string;
  class_id: string;
  class_name: string;
}

/** GET a section's weekly grid for a year (rich join). */
export function useSectionTimetable(
  schoolId: string | undefined,
  sectionId: string | undefined,
  yearId: string | undefined,
) {
  return useQuery<SectionTimetableRow[]>({
    queryKey: academicKeys.sectionTimetable(schoolId ?? '', sectionId ?? '', yearId ?? ''),
    queryFn: () =>
      getOne<SectionTimetableRow[]>(
        `/schools/${schoolId}/sections/${sectionId}/timetable/${yearId}`,
      ),
    enabled: Boolean(schoolId) && Boolean(sectionId) && Boolean(yearId),
    staleTime: 1000 * 30,
  });
}

/** GET school-wide occupancy for a year — every slot, for the clash index. */
export function useSchoolTimetable(schoolId: string | undefined, yearId: string | undefined) {
  return useQuery<SchoolTimetableRow[]>({
    queryKey: academicKeys.schoolTimetable(schoolId ?? '', yearId ?? ''),
    queryFn: () =>
      getOne<SchoolTimetableRow[]>(`/schools/${schoolId}/timetable?year_id=${yearId}`),
    enabled: Boolean(schoolId) && Boolean(yearId),
    staleTime: 1000 * 30,
  });
}

export interface UpsertSlotBody {
  subject_id: string;
  teacher_staff_id: string;
  room_id?: string | null;
}

export interface UpsertSlotVars {
  dayOfWeek: number;
  periodNo: number;
  body: UpsertSlotBody;
  /** Full display row applied optimistically to the section-grid cache. */
  optimistic: SectionTimetableRow;
}

/**
 * PUT a single cell (idempotent upsert). Optimistically writes the display row
 * into the section-grid cache, rolls back on error (409 CLASH_*), and refreshes
 * both the grid and the school occupancy index on settle.
 */
export function useUpsertTimetableSlot(schoolId: string, sectionId: string, yearId: string) {
  const queryClient = useQueryClient();
  const gridKey = academicKeys.sectionTimetable(schoolId, sectionId, yearId);
  return useMutation<
    unknown,
    Error,
    UpsertSlotVars,
    { previous: SectionTimetableRow[] | undefined }
  >({
    mutationFn: ({ dayOfWeek, periodNo, body }) =>
      put<UpsertSlotBody, unknown>(
        `/schools/${schoolId}/sections/${sectionId}/timetable/${yearId}/${dayOfWeek}/${periodNo}`,
        body,
      ),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: gridKey });
      const previous = queryClient.getQueryData<SectionTimetableRow[]>(gridKey);
      queryClient.setQueryData<SectionTimetableRow[]>(gridKey, (old = []) => [
        ...old.filter(
          (r) => !(r.day_of_week === vars.dayOfWeek && r.period_no === vars.periodNo),
        ),
        vars.optimistic,
      ]);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(gridKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: gridKey });
      queryClient.invalidateQueries({ queryKey: academicKeys.schoolTimetable(schoolId, yearId) });
    },
  });
}

/** DELETE a cell (clear). Optimistically drops it from the section-grid cache. */
export function useDeleteTimetableSlot(schoolId: string, sectionId: string, yearId: string) {
  const queryClient = useQueryClient();
  const gridKey = academicKeys.sectionTimetable(schoolId, sectionId, yearId);
  return useMutation<
    void,
    Error,
    { dayOfWeek: number; periodNo: number },
    { previous: SectionTimetableRow[] | undefined }
  >({
    mutationFn: ({ dayOfWeek, periodNo }) =>
      del(`/schools/${schoolId}/sections/${sectionId}/timetable/${yearId}/${dayOfWeek}/${periodNo}`),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: gridKey });
      const previous = queryClient.getQueryData<SectionTimetableRow[]>(gridKey);
      queryClient.setQueryData<SectionTimetableRow[]>(gridKey, (old = []) =>
        old.filter((r) => !(r.day_of_week === vars.dayOfWeek && r.period_no === vars.periodNo)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(gridKey, ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: gridKey });
      queryClient.invalidateQueries({ queryKey: academicKeys.schoolTimetable(schoolId, yearId) });
    },
  });
}

// ---- Substitute assignments (gen.ListSubstitutesByDateRow) -------------------
//
// A cover assignment is per (timetable_slot, on_date). The list join carries
// section/subject/substitute labels. on_date arrives as pgtype.Date — the day
// view already knows the queried date, so we display that rather than decoding
// the wire shape.

export interface PgDate {
  Time: string;
  Valid: boolean;
  InfinityModifier: number;
}

export interface SubstituteRow {
  id: string;
  org_id: string;
  school_id: string;
  timetable_slot_id: string;
  on_date: PgDate;
  period_no: number;
  substitute_teacher_staff_id: string;
  leave_request_id: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
  section_id: string;
  day_of_week: number;
  subject_id: string;
  subject_name: string;
  substitute_name: string;
}

/** GET /schools/:schoolID/substitutes?date=YYYY-MM-DD — the day's cover board. */
export function useSubstitutes(schoolId: string | undefined, date: string) {
  return useQuery<SubstituteRow[]>({
    queryKey: academicKeys.substitutes(schoolId ?? '', date),
    queryFn: () => getOne<SubstituteRow[]>(`/schools/${schoolId}/substitutes?date=${date}`),
    enabled: Boolean(schoolId) && Boolean(date),
    staleTime: 1000 * 30,
  });
}

export interface CreateSubstituteInput {
  timetable_slot_id: string;
  on_date: string; // "YYYY-MM-DD"
  period_no: number;
  substitute_teacher_staff_id: string;
  reason?: string | null;
}

export function useCreateSubstitute(schoolId: string, date: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, CreateSubstituteInput>({
    mutationFn: (input) =>
      post<CreateSubstituteInput, unknown>(`/schools/${schoolId}/substitutes`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.substitutes(schoolId, date) });
    },
  });
}

// ---- My timetable (gen.GetTeacherTimetableRow — self-scoped) ----------------
//
// GET /staff/me/timetable?year_id= (academic/handler.go GetMyTimetable, Phase C
// item 2f): the authed staff member's OWN weekly grid, no permission gate (the
// service self-scopes to actor.ID). The row shape is field-for-field identical
// to SchoolTimetableRow above (verified against gen.GetTeacherTimetableRow in
// repo/gen/academic.sql.go: id, org_id, school_id, section_id,
// academic_year_id, day_of_week, period_no, subject_id, subject_name,
// teacher_staff_id, teacher_name, room_id, room_name, section_name, class_id,
// class_name, starts_at, ends_at, created_at, updated_at) so it is aliased
// rather than redeclared.

export type MyTimetableRow = SchoolTimetableRow;

/**
 * GET the current staff member's own weekly grid. yearId is optional — when
 * omitted the server defaults to the actor's current year(s) (is_current).
 */
export function useMyTimetable(yearId: string | undefined) {
  return useQuery<MyTimetableRow[]>({
    queryKey: academicKeys.myTimetable(yearId),
    queryFn: () =>
      getOne<MyTimetableRow[]>(
        yearId ? `/staff/me/timetable?year_id=${yearId}` : '/staff/me/timetable',
      ),
    staleTime: 1000 * 30,
  });
}

// ---- Elective groups (gen.ElectiveGroup / student subject baskets) ----------
//
// Reads gate on timetable.view (schoolAcad.GET, router.go:646-651), writes
// gate on settings.manage — the same split every other config tab on this
// page uses (canManage prop), just enforced service-side here rather than by
// a shared `canManage` middleware helper (service.go:1002-1173). A group is
// scoped to one (class_id, academic_year_id); ChooseElective records one
// subject choice per (enrollment, group) — the server 409s past max_choices.

export interface ElectiveGroup {
  id: string;
  org_id: string;
  school_id: string;
  class_id: string;
  academic_year_id: string;
  name: string;
  min_choices: number;
  max_choices: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface ElectiveGroupSubjectRow {
  org_id: string;
  elective_group_id: string;
  subject_id: string;
  subject_name: string;
  created_at: string;
  updated_at: string;
}

export interface StudentElective {
  id: string;
  org_id: string;
  student_enrollment_id: string;
  elective_group_id: string;
  subject_id: string;
  created_at: string;
  updated_at: string;
}

export function useElectiveGroups(
  schoolId: string | undefined,
  classId: string | undefined,
  yearId: string | undefined,
) {
  return useQuery<PagedResult<ElectiveGroup>>({
    queryKey: academicKeys.electiveGroups(schoolId ?? '', classId, yearId),
    queryFn: () =>
      getPaginated<ElectiveGroup>(`/schools/${schoolId}/elective-groups`, {
        per_page: 200,
        ...(classId ? { class_id: classId } : {}),
        ...(yearId ? { academic_year_id: yearId } : {}),
      }),
    enabled: Boolean(schoolId),
    staleTime: 1000 * 30,
  });
}

export interface CreateElectiveGroupInput {
  class_id: string;
  academic_year_id: string;
  name: string;
  min_choices: number;
  max_choices: number;
}

export interface UpdateElectiveGroupInput {
  name?: string;
  min_choices?: number;
  max_choices?: number;
}

function invalidateElectiveGroups(queryClient: ReturnType<typeof useQueryClient>, schoolId: string) {
  queryClient.invalidateQueries({ queryKey: ['academic', 'elective-groups', schoolId] });
}

export function useCreateElectiveGroup(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<ElectiveGroup, Error, CreateElectiveGroupInput>({
    mutationFn: (input) =>
      post<CreateElectiveGroupInput, ElectiveGroup>(`/schools/${schoolId}/elective-groups`, input),
    onSuccess: () => invalidateElectiveGroups(queryClient, schoolId),
  });
}

export function useUpdateElectiveGroup(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<ElectiveGroup, Error, { id: string; patch: UpdateElectiveGroupInput }>({
    mutationFn: ({ id, patch: body }) =>
      patch<UpdateElectiveGroupInput, ElectiveGroup>(`/schools/${schoolId}/elective-groups/${id}`, body),
    onSuccess: () => invalidateElectiveGroups(queryClient, schoolId),
  });
}

export function useDeleteElectiveGroup(schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del(`/schools/${schoolId}/elective-groups/${id}`),
    onSuccess: () => invalidateElectiveGroups(queryClient, schoolId),
  });
}

export function useElectiveGroupSubjects(schoolId: string, groupId: string | undefined) {
  return useQuery<ElectiveGroupSubjectRow[]>({
    queryKey: academicKeys.electiveGroupSubjects(schoolId, groupId ?? ''),
    queryFn: () =>
      getOne<ElectiveGroupSubjectRow[]>(`/schools/${schoolId}/elective-groups/${groupId}/subjects`),
    enabled: Boolean(schoolId) && Boolean(groupId),
    staleTime: 1000 * 30,
  });
}

export function useAddElectiveGroupSubject(schoolId: string, groupId: string) {
  const queryClient = useQueryClient();
  return useMutation<ElectiveGroupSubjectRow, Error, string>({
    mutationFn: (subjectId) =>
      post<{ subject_id: string }, ElectiveGroupSubjectRow>(
        `/schools/${schoolId}/elective-groups/${groupId}/subjects`,
        { subject_id: subjectId },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.electiveGroupSubjects(schoolId, groupId) });
    },
  });
}

export function useRemoveElectiveGroupSubject(schoolId: string, groupId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (subjectId) =>
      del(`/schools/${schoolId}/elective-groups/${groupId}/subjects/${subjectId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: academicKeys.electiveGroupSubjects(schoolId, groupId) });
    },
  });
}

/** POST /schools/:schoolID/elective-groups/:groupID/choices — records one
 * student's subject pick within the group. Invalidates nothing group-shaped
 * (choices aren't listed anywhere yet, per-student state lives on the caller). */
export function useChooseElective(schoolId: string, groupId: string) {
  return useMutation<StudentElective, Error, { enrollmentId: string; subjectId: string }>({
    mutationFn: ({ enrollmentId, subjectId }) =>
      post<{ enrollment_id: string; subject_id: string }, StudentElective>(
        `/schools/${schoolId}/elective-groups/${groupId}/choices`,
        { enrollment_id: enrollmentId, subject_id: subjectId },
      ),
  });
}
